import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import ParkingCard from "../components/ParkingCard";
import { Search, SlidersHorizontal, X, MapPin, Map, LayoutGrid, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

// Lazy load the map component
const SpotMap = lazy(() => import("../components/SpotMap"));

import { API } from "../utils/api";
import { trackSearch, trackAddToWishlist } from "../utils/metaPixel";

const FEATURE_OPTIONS = [
  "Cubierta",
  "Segura",
  "24h",
  "Vigilada",
  "Cámaras",
  "Cargador EV",
  "Fácil acceso",
  "Portero"
];

export default function Explore() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [spots, setSpots] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "map"
  
  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    city: searchParams.get("city") || "",
    minPrice: 0,
    maxPrice: 10,
    features: [],
    sortBy: "rating",
    hasPlazafyGate: false
  });

  useEffect(() => {
    fetchSpots();
    if (user) fetchFavoriteIds();
  }, [user]);

  // El valor inicial de filters.search ya se lee desde la URL en useState (línea ~50).
  // No re-sincronizamos automáticamente en cada cambio, porque si lo hiciéramos,
  // el usuario no podría borrar lo que escribió (la URL aún contiene la búsqueda anterior
  // y el efecto lo restauraría). La URL se actualiza únicamente al hacer "Buscar".

  const fetchSpots = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append("search", filters.search);
      if (filters.city) params.append("city", filters.city);
      if (filters.minPrice > 0) params.append("min_price", filters.minPrice);
      if (filters.maxPrice < 10) params.append("max_price", filters.maxPrice);
      if (filters.features.length > 0) params.append("features", filters.features.join(","));
      if (filters.hasPlazafyGate) params.append("has_plazafy_gate", "true");

      const response = await axios.get(`${API}/spots?${params.toString()}`);
      let sortedSpots = response.data;

      if (filters.sortBy === "price_low") {
        sortedSpots.sort((a, b) => a.price_hour - b.price_hour);
      } else if (filters.sortBy === "price_high") {
        sortedSpots.sort((a, b) => b.price_hour - a.price_hour);
      } else {
        sortedSpots.sort((a, b) => b.rating - a.rating);
      }

      setSpots(sortedSpots);
    } catch (error) {
      console.error("Error fetching spots:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFavoriteIds = async () => {
    try {
      const res = await axios.get(`${API}/favorites/ids`);
      setFavoriteIds(res.data);
    } catch (e) {
      // Not logged in or error - no favorites
    }
  };

  const toggleFavorite = async (spotId) => {
    if (!user) {
      toast.error("Inicia sesión para guardar favoritos");
      return;
    }
    const isFav = favoriteIds.includes(spotId);
    try {
      if (isFav) {
        await axios.delete(`${API}/favorites/${spotId}`);
        setFavoriteIds(prev => prev.filter(id => id !== spotId));
        toast.success("Eliminado de favoritos");
      } else {
        await axios.post(`${API}/favorites/${spotId}`);
        setFavoriteIds(prev => [...prev, spotId]);
        const spot = spots.find(s => s.id === spotId);
        trackAddToWishlist(spotId, spot?.title, spot?.price_hour);
        toast.success("Añadido a favoritos");
      }
    } catch (e) {
      toast.error("Error al actualizar favoritos");
    }
  };


  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams(filters.search ? { search: filters.search } : {});
    if (filters.search) trackSearch(filters.search);
    fetchSpots();
  };

  const handleFeatureToggle = (feature) => {
    setFilters(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      city: "",
      minPrice: 0,
      maxPrice: 10,
      features: [],
      sortBy: "rating",
      hasPlazafyGate: false
    });
    setSearchParams({});
  };

  const hasActiveFilters = filters.search || filters.city || filters.minPrice > 0 || 
    filters.maxPrice < 10 || filters.features.length > 0 || filters.hasPlazafyGate;

  return (
    <div className="pt-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">{t('explore.title')}</h1>
          <p className="text-gray-400">{t('explore.subtitle')}</p>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex flex-col lg:flex-row gap-4 mb-8">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder={t('explore.searchPlaceholder') || "Buscar por ubicación, dirección..."}
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-12 h-12 bg-[#231f30] border-white/10 text-white placeholder:text-gray-500"
                data-testid="explore-search-input"
              />
            </div>
            <Button 
              type="submit"
              className="h-12 px-6 bg-violet-500 hover:bg-violet-600 text-white"
              data-testid="explore-search-btn"
            >
              {t('common.search')}
            </Button>
          </form>

          <div className="flex gap-2">
            {/* View Mode Toggle */}
            <div className="flex bg-[#231f30] border border-white/10 rounded-lg p-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode("grid")}
                className={`px-3 h-10 rounded-md ${viewMode === "grid" ? "bg-violet-500 text-white" : "text-gray-400 hover:text-white"}`}
                data-testid="grid-view-btn"
              >
                <LayoutGrid className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode("map")}
                className={`px-3 h-10 rounded-md ${viewMode === "map" ? "bg-violet-500 text-white" : "text-gray-400 hover:text-white"}`}
                data-testid="map-view-btn"
              >
                <Map className="w-5 h-5" />
              </Button>
            </div>

            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="h-12 bg-[#231f30] border-white/10 text-white hover:bg-white/5"
              data-testid="toggle-filters-btn"
            >
              <SlidersHorizontal className="w-5 h-5 mr-2" />
              {t('explore.filters')}
              {hasActiveFilters && (
                <span className="ml-2 w-5 h-5 rounded-full bg-violet-500 text-xs flex items-center justify-center">
                  {filters.features.length + (filters.search ? 1 : 0) + (filters.minPrice > 0 || filters.maxPrice < 10 ? 1 : 0) + (filters.hasPlazafyGate ? 1 : 0)}
                </span>
              )}
            </Button>

            <Select value={filters.sortBy} onValueChange={(value) => setFilters(prev => ({ ...prev, sortBy: value }))}>
              <SelectTrigger className="h-12 w-[180px] bg-[#231f30] border-white/10 text-white" data-testid="sort-select">
                <SelectValue placeholder={t('explore.sortBy')} />
              </SelectTrigger>
              <SelectContent className="bg-[#231f30] border-white/10">
                <SelectItem value="rating" className="text-white hover:bg-white/5">{t('explore.bestRated')}</SelectItem>
                <SelectItem value="price_low" className="text-white hover:bg-white/5">{t('explore.priceLowHigh')}</SelectItem>
                <SelectItem value="price_high" className="text-white hover:bg-white/5">{t('explore.priceHighLow')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="dashboard-card mb-8 animate-fadeIn relative z-20">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white">{t('explore.filters')}</h3>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-gray-400 hover:text-white"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  {t('explore.clearFilters')}
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* City filter */}
              <div>
                <Label className="text-gray-300 mb-2 block">{t('explore.city')}</Label>
                <Input
                  type="text"
                  placeholder="Ej: Madrid"
                  value={filters.city}
                  onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value }))}
                  className="bg-[#15121e] border-white/10 text-white placeholder:text-gray-500"
                  data-testid="city-filter-input"
                />
              </div>

              {/* Price range */}
              <div>
                <Label className="text-gray-300 mb-2 block">
                  {t('explore.pricePerHour')}: €{filters.minPrice} - €{filters.maxPrice}
                </Label>
                <div className="pt-2 px-2">
                  <Slider
                    min={0}
                    max={10}
                    step={0.5}
                    value={[filters.minPrice, filters.maxPrice]}
                    onValueChange={([min, max]) => setFilters(prev => ({ ...prev, minPrice: min, maxPrice: max }))}
                    className="w-full"
                    data-testid="price-slider"
                  />
                </div>
              </div>

              {/* Features */}
              <div className="md:col-span-2 lg:col-span-1">
                <Label className="text-gray-300 mb-2 block">{t('explore.features')}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {FEATURE_OPTIONS.map((feature) => (
                    <div key={feature} className="flex items-center gap-2">
                      <Checkbox
                        id={feature}
                        checked={filters.features.includes(feature)}
                        onCheckedChange={() => handleFeatureToggle(feature)}
                        className="border-white/20 data-[state=checked]:bg-violet-500"
                        data-testid={`feature-${feature.toLowerCase().replace(' ', '-')}`}
                      />
                      <Label htmlFor={feature} className="text-sm text-gray-400 cursor-pointer">
                        {feature}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Plazafy Gate filter */}
              <div className="md:col-span-2 lg:col-span-3">
                <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-violet-500/10 to-emerald-500/10 rounded-lg border border-violet-500/20">
                  <Checkbox
                    id="plazafy-gate-filter"
                    checked={filters.hasPlazafyGate}
                    onCheckedChange={(checked) => setFilters(prev => ({ ...prev, hasPlazafyGate: checked }))}
                    className="border-violet-500/50 data-[state=checked]:bg-violet-500"
                    data-testid="plazafy-gate-filter"
                  />
                  <div className="flex-1">
                    <Label htmlFor="plazafy-gate-filter" className="text-white font-medium cursor-pointer flex items-center gap-2">
                      Solo plazas con Plazafy Gate
                      <span className="px-1.5 py-0.5 bg-violet-500 text-white text-[10px] rounded-full font-medium">
                        Nuevo
                      </span>
                    </Label>
                    <p className="text-gray-400 text-sm mt-0.5">
                      Filtra por plazas con control de acceso inteligente
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button
                onClick={fetchSpots}
                className="bg-violet-500 hover:bg-violet-600 text-white"
                data-testid="apply-filters-btn"
              >
                {t('explore.applyFilters')}
              </Button>
            </div>
          </div>
        )}

        {/* Results */}
        <div className="mb-4">
          <p className="text-gray-400">
            {loading ? t('common.loading') : t('explore.spotsFound', { count: spots.length })}
          </p>
        </div>

        {/* Grid View */}
        {viewMode === "grid" && (
          <>
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="parking-card">
                    <div className="skeleton h-48" />
                    <div className="p-4 space-y-3">
                      <div className="skeleton h-5 w-3/4 rounded" />
                      <div className="skeleton h-4 w-1/2 rounded" />
                      <div className="skeleton h-6 w-1/4 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : spots.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="spots-grid">
                {spots.map((spot) => (
                  <ParkingCard key={spot.id} spot={spot} isFavorite={favoriteIds.includes(spot.id)} onToggleFavorite={toggleFavorite} />
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <MapPin className="w-10 h-10 text-gray-500" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{t('explore.noResults')}</h3>
                <p className="text-gray-400 mb-6">{t('explore.tryOtherFilters')}</p>
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="border-white/10 text-white hover:bg-white/5"
                >
                  {t('explore.clearFilters')}
                </Button>
              </div>
            )}
          </>
        )}

        {/* Map View */}
        {viewMode === "map" && (
          <div className="h-[600px] rounded-xl overflow-hidden relative z-0 isolate" data-testid="map-container">
            {loading ? (
              <div className="w-full h-full bg-[#231f30] flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-violet-500 animate-spin" />
              </div>
            ) : (
              <Suspense fallback={
                <div className="w-full h-full bg-[#231f30] flex items-center justify-center">
                  <Loader2 className="w-10 h-10 text-violet-500 animate-spin" />
                </div>
              }>
                <SpotMap spots={spots} />
              </Suspense>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
